import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Check, 
  ArrowRight, 
  Shield, 
  Headphones, 
  Clock, 
  Users,
  Server,
  Lock,
  RefreshCw,
  BarChart3,
  FileText,
  Zap,
  Crown,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';

const Maintenance = () => {
  const securityFeatures = [
    "Premium hosting included",
    "SSL certificate management",
    "Weekly automated backups",
    "Security monitoring & alerts",
    "Malware scanning & removal",
    "WordPress core updates",
    "Plugin security updates",
    "Uptime monitoring (99.9%)",
    "Basic performance optimization",
    "Monthly security report"
  ];

  const proFeatures = [
    "Everything in Security Essential",
    "1 hour monthly content updates",
    "Priority technical support",
    "Advanced performance optimization",
    "SEO monitoring & adjustments",
    "Broken link detection & fixes",
    "Image optimization",
    "Database cleanup & optimization",
    "Custom backup scheduling",
    "Detailed monthly reports",
    "Emergency support access",
    "Phone support included"
  ];

  const maintenanceBenefits = [
    {
      title: "Peace of Mind",
      description: "Sleep better knowing your website is secure, backed up, and monitored 24/7",
      icon: <Shield className="h-6 w-6 text-accent" />
    },
    {
      title: "Save Time",
      description: "Focus on your business while we handle all the technical maintenance",
      icon: <Clock className="h-6 w-6 text-accent" />
    },
    {
      title: "Prevent Problems",
      description: "Proactive maintenance prevents costly emergencies and downtime",
      icon: <AlertTriangle className="h-6 w-6 text-accent" />
    },
    {
      title: "Stay Current",
      description: "Regular updates keep your site secure and running on the latest technology",
      icon: <RefreshCw className="h-6 w-6 text-accent" />
    }
  ];

  const whatWeMonitor = [
    "Website uptime and performance",
    "Security threats and vulnerabilities", 
    "Backup integrity and restoration",
    "Plugin and theme compatibility",
    "Page load speeds and optimization",
    "SEO performance and rankings",
    "Broken links and 404 errors",
    "Database performance and cleanup"
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Website Maintenance & Support
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Keep Your Website
            <span className="text-gradient"> Running Perfectly</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Don't let technical issues slow down your business. Our maintenance plans ensure 
            your website stays secure, fast, and always available for your customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
            >
              <Link to="/booking">
                Get Website Health Check
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg" 
              className="font-heading font-semibold"
            >
              <Link to="#plans">View Plans</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Maintenance Matters */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Website Maintenance Matters
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Your website is a critical business asset. Like any valuable asset, 
              it needs regular care to perform at its best.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {maintenanceBenefits.map((benefit, index) => (
              <Card key={index} className="hover-lift shadow-card text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    {benefit.title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance Plans */}
      <section id="plans" className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Choose Your Maintenance Plan
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              From basic security to comprehensive support, we have a plan that fits your needs and budget.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* Security Essential Plan */}
            <Card className="relative hover-lift shadow-card border-2 border-border">
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">Security Essential</CardTitle>
                <p className="font-body text-muted-foreground">Complete protection and peace of mind</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-primary">$99</span>
                  <span className="font-body text-muted-foreground ml-2">- $150/mo</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Everything you need to keep your site secure
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {securityFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start Security Essential
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Perfect for: Small businesses, blogs, basic websites
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Pro Partner Plan */}
            <Card className="relative hover-lift shadow-elegant border-2 border-accent bg-gradient-to-br from-white to-accent/5">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-accent text-accent-foreground font-body font-semibold px-4 py-1">
                  Most Popular
                </Badge>
              </div>
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Headphones className="h-8 w-8 text-accent" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">Pro Partner</CardTitle>
                <p className="font-body text-muted-foreground">Complete maintenance + ongoing support</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-accent">$250</span>
                  <span className="font-body text-muted-foreground ml-2">- $500/mo</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Full-service maintenance with content updates
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {proFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start Pro Partner
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Perfect for: Growing businesses, e-commerce, professional services
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enterprise Option */}
          <div className="mt-12 text-center">
            <Card className="max-w-2xl mx-auto shadow-elegant bg-gradient-to-r from-primary/10 to-accent/10">
              <CardContent className="p-8">
                <Crown className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                  Enterprise Maintenance
                </h3>
                <p className="font-body text-muted-foreground mb-6">
                  Large websites, multiple sites, or complex applications need custom maintenance plans. 
                  Let's discuss a solution tailored to your specific requirements.
                </p>
                <Button 
                  asChild 
                  variant="outline" 
                  className="font-heading font-semibold"
                >
                  <Link to="/booking">Discuss Enterprise Plan</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Monitor */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              What We Monitor & Maintain
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive monitoring ensures problems are caught and fixed 
              before they impact your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whatWeMonitor.map((item, index) => (
              <Card key={index} className="hover-lift shadow-card">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="font-body text-foreground">{item}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Support */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Emergency Support When You Need It
            </h2>
            <p className="font-body text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              Website emergencies don't wait for business hours. Our emergency support 
              ensures you're never left stranded.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">Rapid Response</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Critical issues get immediate attention, typically resolved within 2-4 hours.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Server className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">Site Recovery</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                If your site goes down, we'll restore it from our secure backups quickly.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">Security Incidents</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Malware, hacks, or security breaches are handled immediately by our experts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance vs DIY */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Professional Maintenance vs. DIY
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              While you could handle maintenance yourself, here's why most business owners 
              choose to let the experts handle it.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* DIY Challenges */}
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-foreground flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                  DIY Maintenance Challenges
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="font-body text-sm text-muted-foreground">
                    Time-consuming technical tasks take you away from your business
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="font-body text-sm text-muted-foreground">
                    Risk of breaking your site with incorrect updates
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="font-body text-sm text-muted-foreground">
                    Missing security vulnerabilities until it's too late
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="font-body text-sm text-muted-foreground">
                    No backup strategy when disasters strike
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="font-body text-sm text-muted-foreground">
                    Steep learning curve for technical maintenance tasks
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Professional Benefits */}
            <Card className="shadow-card bg-gradient-to-br from-accent/5 to-primary/5">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-foreground flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-accent mr-2" />
                  Professional Maintenance Benefits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-foreground">
                    Focus on your business while experts handle the technical details
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-foreground">
                    Professional testing ensures updates don't break your site
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-foreground">
                    Proactive security monitoring prevents attacks before they happen
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-foreground">
                    Automated backups and disaster recovery plans protect your investment
                  </span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-foreground">
                    20 years of experience handling every possible website issue
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Ready for Peace of Mind?
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Stop worrying about your website and start focusing on your business. 
              Our maintenance plans ensure your site is always secure, fast, and available.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Get Free Website Health Check
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/web-development">View Development Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Maintenance;